<template>
  <VCol v-if="isMounted" cols="auto" class="py-0" align-self="center">
    <TransitionOverlay
      :display="highlightDemo"
      pink
      fill-width
      full-height
      name="highlight-demo"
      data-testid="highlight-demo"
    />
    <SDialog
      v-model:show-dialog="dialog"
      :width="375"
      card-max-height="700"
      scrollable
      class="demonstrator-dialog-overlay"
      :aria-close="$t('dialog.close.aria-label')"
    >
      <template #activator>
        <VRow
          no-gutters
          class=" shrink"
          data-testid="header-demonstrator"
          align="center"
          @click="openMenu"
        >
          <VCol :cols=" border ? '12' : 'auto'"  class="d-flex align-center" style="line-height: 1.1; position: relative">
            <div
              v-if="hasDemonstrator"
              class="font-weight-medium text-greyedOut font14"
              style="max-width:250px"
            >
              {{ cartStore.isSharedCartMode ? $t('customerCart.p.cartPreparedBy') : $t("demonstrator.label.shoppingWith") }}
              <SBtn
                v-if="hasDemonstrator"
                class="font-weight-bold text-initial"
                :class="{'text-underline--none': isDisplayOnly}"
                style="margin-bottom:2px;"
                link-medium
              >
                <label>{{ demonstratorName }}</label>
              </SBtn>
              <DemonstratorAvatar
                :demonstrator="shoppingWith"
                :size="38"
                style="border:2px solid; border-color: white"
              />
            </div>
            <template v-else>
              <SBtn
                v-if="!hasDemonstrator && !isDisplayOnly"
                class="text-initial"
                :variant="border ? 'outlined' : undefined"
                rounded
                link-small
                :block="border"
                data-testid="find-demonstrator"
                @click.prevent="changeDemonstrator"
              >
                <span :class="{'font-weight-bold' : border, 'font-weight-medium text-greyedOut find-demo-label': !border}" class="link-hover">{{ $t("demonstrator.label.find") }}</span>
              </SBtn>
            </template>
          </VCol>
        </VRow>
      </template>
      <VCardTitle
        v-if="showDemoFinder"
        class="text-center pt-0 pb-1 text-primary"
      >
        <h3>
          {{ $t('demonstrator.label.selectStampin') }}
          <STooltip callout size="1.7rem" class="pb-12 mb-12">
            {{ $t('demonstrator.tooltip.help') }}
          </STooltip>
        </h3>
      </VCardTitle>
      <VCardText class="px-0 py-1 text-primary">
        <VRow
          v-if="hasDemonstrator && !showDemoFinder && demonstrator"
          no-gutters
          justify="center"
        >
          <SLoadingOverlay :loading="loading" />
          <VCol class="demonstrator-grid">
            <h4 class="mt-3 text-center">
              {{ $t("demonstrator.label.shoppingWith") }}
            </h4>
            <DemonstratorContent :demonstrator="demonstrator" :can-clear="!isDisplayOnly" @change-demo="changeDemonstrator" />
          </VCol>
        </VRow>
        <VRow
          v-if="showDemoFinder"
          no-gutters
          class="pb-2"
        >
          <VCol>
            <SLoadingOverlay :loading="loading" />
            <!-- Lazy: the finder (search by name and by location) only loads once a
                 visitor opens it, not in every page's entry chunk. -->
            <LazyAccountDemonstratorFinder
              @demo-selected="demoSelected"
            />
          </VCol>
        </VRow>
      </VCardText>
    </SDialog>
  </VCol>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import type { Demonstrator } from '~/types/account'
import DemonstratorAvatar from '~/components/account/demonstrator/DemonstratorAvatar.vue'
import DemonstratorContent from '~/components/account/demonstrator/DemonstratorContent.vue'
import TransitionOverlay from '~/components/base/TransitionOverlay.vue'

import { assertIsDefined } from '~/utils/assert'
import { useCartStore } from '~/stores/cart'
import { usePersistentStore } from '~/stores/persistent'

interface Props {
  displayOnly?: boolean
  border?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  displayOnly: false,
  border: false
})
const dialogShow = ref(false)
const isMounted = ref(false)
const showDemoFinder = ref(false)
const highlightDemo = ref(false)
const shoppingWith = ref<Demonstrator | null>(null)

const cartStore = useCartStore()
const persistentStore = usePersistentStore()
const { demonstrator } = storeToRefs(cartStore)

// A shared cart is locked to the demonstrator who prepared it: the shopper may
// see them but not remove or change them, which would change the order's
// attribution. nuxt2 PR 21680 (AB#58175). AB#64449
const isDisplayOnly = computed(() => props.displayOnly || cartStore.isSharedCartMode)

const persistentHighlightDemo = computed(() => persistentStore.getPersistentValue('highlightDemo'))

const hasDemonstrator = computed(() => (shoppingWith.value?.demoId || 0) > 0)
const demonstratorName = computed(() => props.border
  ? `${shoppingWith.value?.firstName ?? ''} ${shoppingWith.value?.lastName ?? ''}`.trim()
  : (shoppingWith.value?.firstName ?? ''))

const dialog = computed({
  get () {
    return dialogShow.value
  },
  set (newValue) {
    dialogShow.value = newValue
  }
})
const openMenu = () => {
  if (isDisplayOnly.value) {
    return
  }
  dialog.value = true
  showDemoFinder.value = !hasDemonstrator.value
}

const openFromContentPage = () => {
  dialog.value = true
  openMenu()
}
const changeDemonstrator = async () => {
  if (isDisplayOnly.value) { return }
  if (hasDemonstrator.value && demonstrator.value) {
    await clearDemo()
  }

  showDemoFinder.value = true
}

const { processAction, loading } = useAction()

const clearDemo = () => processAction(async () => {
  assertIsDefined(demonstrator.value, 'demo should be defined in demo header for clearing')
  await cartStore.removeDemoId()
  showDemoFinder.value = true
  openMenu()
})

const demoSelected = (demonstratorNew: Demonstrator) => processAction(async () => {
  if (demonstratorNew?.demoId > 0) {
    await cartStore.updateDemoId(demonstratorNew.demoId)
    shoppingWith.value = demonstrator.value
    showDemoFinder.value = false
  }
})

onMounted(() => {
  if (persistentHighlightDemo.value) {
    setTimeout(() => {
      highlightDemo.value = true
    }, 300)
    setTimeout(() => {
      persistentStore.updateValue('highlightDemo', false)
      highlightDemo.value = false
    }, 3000)
  }
  isMounted.value = true
  shoppingWith.value = demonstrator.value
  useListen('open-demofinder', openFromContentPage)
})

onBeforeUnmount(() => {
  useUnlisten('open-demofinder', openFromContentPage)
})

watch(persistentHighlightDemo, () => {
  highlightDemo.value = persistentHighlightDemo.value ?? false
})

watch(demonstrator, () => {
  shoppingWith.value = demonstrator.value
})
</script>
<style lang="scss" scoped>
  :global(.demonstrator-dialog-overlay.v-overlay.v-dialog) {
    align-items: start;
    margin-top: 10%;
  }
  // we have this class but needed here because of priority of css to override underline from link
  .text-underline--none {
    text-decoration: none;
  }

  // Vuetify 2 emitted `d-grid`; Vuetify 4 does not, so the dialog column keeps
  // its grid layout through this app-owned semantic class.
  .demonstrator-grid {
    display: grid;
  }

  // Mirror HeaderTopLink's `.label-light { font-size: 1rem; line-height: 1 }`
  // so Find-a-Demonstrator matches Rewards/Join/Blog. The button keeps
  // `link-small` (needed for the underline-link treatment), so without the
  // font-size override the inner span inherits link-small's 0.8571rem (12px);
  // restating 1rem here brings it to 14px like its siblings.
  //
  // margin-top nudges the label down to sit exactly level with the sibling
  // links (its line box tops out ~1px above them). The nudge must live on the
  // span, not the outer VCol: that col is align-self:center, which absorbs col
  // padding and leaves the label unmoved. 3px lands it level — the same
  // value/lever nuxt2's find-demo span uses. (AB#61473)
  .find-demo-label {
    font-size: 1rem;
    line-height: 1;
    margin-top: 3px;
  }

</style>
